import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Store} from '@ngrx/store';
import * as _ from 'lodash';
import {Subscription} from 'rxjs';
import {Project, ProjectStep} from 'src/app/core/model/project';
import {
  CreateProjectStepAction,
  DeleteProjectStepAction,
  UpdateProjectStepAction,
} from 'src/app/core/store/projects/projects.action';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {MessageService} from 'src/app/services/message.service';
import {
  OphButtonGroupComponent,
  OphButtonGroupOption,
} from 'src/app/shared/design/oph-button-group/oph-button-group.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphInputOrangeComponent} from 'src/app/shared/design/oph-inputs/oph-input-orange/oph-input-orange.component';
import {TaskTriggerSelectionComponent} from 'src/app/shared/tasks/trigger-selection/task-trigger-selection.component';
import {NewTrackerInDialogComponent} from 'src/app/shared/trackers/new-tracker-in-dialog/new-tracker-in-dialog.component';

interface DialogData {
  step: ProjectStep;
  project: Project;
  // If stepIndex is null, it means a new step is being created
  stepIndex: number;
}

@Component({
  selector: 'project-edit-step-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    OphButtonModule,
    OphInputOrangeComponent,
    OphButtonGroupComponent,
    TaskTriggerSelectionComponent,
    OphIconModule,
    MatIconModule,
    NewTrackerInDialogComponent,
  ],
  templateUrl: './project-edit-step-dialog.component.html',
  styleUrl: './project-edit-step-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectEditStepDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<ProjectEditStepDialogComponent>);
  form: FormGroup;
  loadingSave = false;
  booleanOptions: OphButtonGroupOption[] = [{name: 'yes'}, {name: 'no'}];
  formSub = new Subscription();
  confirmView: boolean;
  stepHasTasks: boolean;
  trackerError: boolean = false;
  view = 'step'; // step, newTracker
  newTrackerType: 'activation' | 'completion' | null = null;

  constructor(
    private fb: FormBuilder,
    private store$: Store,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.form = this.getForm(this.data?.step || null);
    this.store$.dispatch(new GetAllTrackersAction({params: {projectId: this.data?.project._id, pageSize: 1000}}));
    this.stepHasTasks = !!this.data.step?.tasks?.length;

    this.formSub.add(
      this.form.get('completionTracker').valueChanges.subscribe(() => {
        this.checkDuplicateTrackers();
      })
    );

    this.formSub.add(
      this.form.get('activationTracker').valueChanges.subscribe(() => {
        this.checkDuplicateTrackers();
      })
    );
  }

  ngOnDestroy() {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }

  getForm(step: ProjectStep | null): FormGroup {
    return this.fb.group({
      name: [step?.name || '', Validators.required],
      activationTracker: this.fb.group({
        trackerId: [step?.activationTracker?.trackerId || null],
        metricId: [step?.activationTracker?.metricId || null],
        operator: [step?.activationTracker?.operator || 'eq'],
        value: [String(step?.activationTracker?.value) || null],
      }),
      completionTracker: this.fb.group({
        trackerId: [step?.completionTracker?.trackerId || null],
        metricId: [step?.completionTracker?.metricId || null],
        operator: [step?.completionTracker?.operator || 'eq'],
        value: [String(step?.completionTracker?.value) || null],
      }),
      previousStepRequired: [step?.previousStepRequired || false],
      tasks: [step?.tasks || []],
    });
  }

  onTypeChange(type: string) {
    this.form.patchValue({type});
    this.form.get('taskId')?.updateValueAndValidity();
    this.form.get('tracker')?.updateValueAndValidity();
  }

  onClearTracker(type: 'completionTracker' | 'activationTracker') {
    this.form.patchValue({[type]: {trackerId: null, metricId: null, operator: 'eq', value: null}});
  }

  onClearActivationTracker() {
    this.form.patchValue({
      activationTracker: {trackerId: null, metricId: null, operator: 'eq', value: null},
    });
  }

  onBooleanOptionChange(option: string, type: string) {
    const value = option === 'yes';
    this.form.patchValue({previousStepRequired: value});
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (!this.confirmView && !this.getTrackerValid('completionTracker')) {
      this.confirmView = true;
      return;
    }

    if (this.trackerError) {
      return;
    }

    this.loadingSave = true;
    const project = this.getUpdatedProject();
    if (this.data.stepIndex === null) {
      // If new
      this.createNewStep(project);
    } else {
      this.updateStep(project);
    }
  }

  getTrackerValid(type: 'completionTracker' | 'activationTracker'): boolean {
    const tracker = this.form.get(type);

    const trackerValid =
      !!tracker.get('trackerId')?.value &&
      !!tracker.get('metricId')?.value &&
      !!tracker.get('operator')?.value &&
      (!!tracker.get('value')?.value || tracker.get('value')?.value === '0');

    return trackerValid;
  }

  createNewStep(project: Project) {
    this.store$.dispatch(
      new CreateProjectStepAction({
        project,
        onSuccess: () => this.onSuccess(),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  updateStep(project: Project) {
    this.store$.dispatch(
      new UpdateProjectStepAction({
        project,
        onSuccess: () => this.onSuccess(),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  onSuccess() {
    this.dialogRef.close();
    this.loadingSave = false;
  }

  onFailure(err: HttpErrorResponse) {
    this.loadingSave = false;
    this.messageService.add(err.error || 'There was a problem updating the project');
  }

  getUpdatedProject(): Project {
    const isNewStep = !!this.data.stepIndex || this.data.stepIndex === 0;
    const steps = _.cloneDeep(this.data.project.steps);
    const step = this.getStepFromForm();
    if (!isNewStep) {
      steps.push(step);
    } else {
      steps[this.data.stepIndex] = step;
    }
    const projectPartial = {
      ...this.data.project,
      steps,
    };
    return projectPartial;
  }

  getStepFromForm(): ProjectStep {
    const step = {...this.data.step, ...this.form.value};
    const isSequential = this.form.get('previousStepRequired').value;

    if (!this.getTrackerValid('completionTracker')) {
      delete step.completionTracker;
    }
    if (isSequential || !this.getTrackerValid('activationTracker')) {
      delete step.activationTracker;
    }
    return step;
  }

  onDelete() {
    const steps = _.cloneDeep(this.data.project.steps);
    steps.splice(this.data.stepIndex, 1);
    // If the new first step had previousStepRequired true, it should be changed to false
    steps[0].previousStepRequired = false;
    const project = {
      ...this.data.project,
      steps,
    };
    this.deleteStep(project);
  }

  deleteStep(project: Project) {
    this.store$.dispatch(
      new DeleteProjectStepAction({
        project,
        onSuccess: () => this.onSuccess(),
        onFailure: err => this.onFailure(err),
      })
    );
  }

  checkDuplicateTrackers() {
    const completionTracker = this.form.get('completionTracker').value;
    const activationTracker = this.form.get('activationTracker').value;

    this.trackerError =
      completionTracker.trackerId &&
      activationTracker.trackerId &&
      completionTracker.trackerId === activationTracker.trackerId &&
      completionTracker.metricId === activationTracker.metricId &&
      completionTracker.operator === activationTracker.operator &&
      completionTracker.value === activationTracker.value;
  }

  onNewTracker(type: 'activation' | 'completion') {
    this.newTrackerType = type;
    this.view = 'newTracker';
  }

  onCloseNewTrackerInDialog(trackerId: string) {
    if (this.newTrackerType === 'activation') {
      this.form.patchValue({
        activationTracker: {trackerId},
      });
    } else {
      this.form.patchValue({
        completionTracker: {trackerId},
      });
    }
    this.view = 'step';
  }
}
